import React, { useState, useEffect } from "react";
import axios from "axios";
import { useParams , useNavigate } from 'react-router-dom';

const FamousPersonality = () => {
    const { id } = useParams(); // Get ID from URL
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [modalImage, setModalImage] = useState(null); // State to manage the modal image
    const navigate = useNavigate();
    const [saveSuccess, setSaveSuccess] = useState("");
    const [saveError, setSaveError] = useState("");

    const [personalities, setPersonalities] = useState([
        { name: "", wikipediaUrl: "", famousFor: "", awards: "", profilePicture: null, profilePictureLink: "" },
    ]);
    const [loadingThumbnail, setLoadingThumbnail] = useState(false);

    const handleInputChange = (e, index) => {
        const { id, value } = e.target;
        const updatedPersonalities = [...personalities];
        updatedPersonalities[index] = { ...updatedPersonalities[index], [id]: value };
        setPersonalities(updatedPersonalities);
    };

    // Fetch personalities from API if ID is present in URL
    useEffect(() => {
        if (id) {
            fetchFamousPersonalities(id);
        }
    }, [id]);

    const fetchFamousPersonalities = async (careerId) => {
        setLoading(true);
        setError(null);
        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_KEY}career/fetchFamousPersonality`,
                new URLSearchParams({
                    career_id: careerId,
                }).toString(),
                {
                    headers: {
                        "Content-Type": "application/x-www-form-urlencoded",
                    },
                }
            );
    
            if (response.data.success) {
                const fetchedPersonalities = response.data.data.map((personality) => ({
                    name: personality.name,
                    wikipediaUrl: personality.wikipedia_url,
                    famousFor: personality.famous_for,
                    awards: personality.awards,
                    profilePicture: null,
                    profilePictureLink: personality.profile_picture,
                    id: personality.id,
                }));
                setPersonalities(fetchedPersonalities);
            } else {
                setError("Failed to fetch famous personalities.");
            }
        } catch (err) {
            setError("Error fetching data from the server.");
            console.error("Error:", err);
        } finally {
            setLoading(false);
        }
    };
    

    const handleFileChange = async (e, index) => {
        const file = e.target.files[0];
        if (!file) return;
    
        // Check if the file type is valid
        const validTypes = ['image/png', 'image/jpeg', 'image/jpg'];
        if (!validTypes.includes(file.type)) {
            alert("Please upload a valid image file (png, jpg, jpeg).");
            return;
        }
    
        setLoadingThumbnail(true);
    
        const formData = new FormData();
        formData.append('image', file);
    
        try {
            const response = await axios.post(`${process.env.REACT_APP_API_KEY}career/uploadFile`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
    
            const updatedPersonalities = [...personalities];
            updatedPersonalities[index] = {
                ...updatedPersonalities[index],
                profilePicture: file,
                profilePictureLink: response.data.link,
            };
            setPersonalities(updatedPersonalities);
    
            setLoadingThumbnail(false);
        } catch (error) {
            console.error("Thumbnail upload failed:", error);
            setLoadingThumbnail(false);
        }
    };
    

    const addMorePersonalities = () => {
        if (personalities.length < 8) {
            setPersonalities([
                ...personalities,
                { name: "", wikipediaUrl: "", famousFor: "", awards: "", profilePicture: null, profilePictureLink: "" },
            ]);
        }
    };

    const handleSave = async () => {
        setSaveSuccess("");
        setSaveError(""); 
        try {
            const promises = personalities.map((personality) => {
                const options = {
                    method: 'POST',
                    url: `${process.env.REACT_APP_API_KEY}career/famousPersonality`,
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded',
                    },
                    data: new URLSearchParams({
                        career_id: id,
                        name: personality.name,
                        wikipedia_url: personality.wikipediaUrl,
                        profile_picture: personality.profilePictureLink,
                        famous_for: personality.famousFor,
                        awards: personality.awards,
                        id: personality.id || '0',
                        isEdit: personality.id ? '1' : '0'
                    }).toString(),
                };

                // Call the API for each personality
                return axios(options);
            });

            // Wait for all the API requests to complete
            const results = await Promise.all(promises);
            results.forEach((response, index) => {
                console.log(`Personality ${index + 1} Saved:`, response.data);
            });
            setSaveSuccess("Data saved successfully!");
            navigate("/career-list");

        } catch (error) {
            setSaveError("Error saving data, please try again.");
            console.error("Error submitting data:", error);
        }
    };

    const removePersonality = async (index, id) => {
        if (!id) return; // Ensure the personality has an ID before trying to delete it
    
        try {
            // Make the API call to remove the personality
            const response = await axios.post(
                `${process.env.REACT_APP_API_KEY}career/removefamousPersonality`,
                new URLSearchParams({
                    id: id,
                }).toString(),
                {
                    headers: {
                        "Content-Type": "application/x-www-form-urlencoded",
                    },
                }
            );
    
            if (response.data.success) {
                console.log(`Personality with ID ${id} removed successfully.`);
                setSaveSuccess("Personality removed successfully.");

                // Remove the personality from the state after successful API call
                const updatedPersonalities = personalities.filter((_, i) => i !== index);
                setPersonalities(updatedPersonalities);
            } else {
                console.error("Failed to remove personality.");
            }
        } catch (error) {
            console.error("Error removing personality:", error);
        }
    };
    

    const openImageModal = (imageUrl) => {
        setModalImage(imageUrl); // Open the modal and set the image URL
    };

    const closeImageModal = () => {
        setModalImage(null); // Close the modal by setting it to null
    };

    const handleCancel = () => {
        navigate("/career-list");
    };

    return (
        <div className="bg-white radius-10 p-4 p-sm-5 shadow">
            {personalities.map((personality, index) => (
                <div key={index} className="row g-4">
                    <div className="col-md-12 d-flex justify-content-between align-items-center">
                        <h2 className="fw-semibold font-24">Famous Personality {index + 1}</h2>
                        {personalities.length > 1 && (
                            <button
                                className="btn btn-danger btn-sm"
                                onClick={() => removePersonality(index, personality.id)}
                            >
                                Remove
                            </button>
                        )}

                    </div>

                    <div className="col-md-6">
                        <label htmlFor="name" className="form-label w-100 font-500 mb-1">Name</label>
                        <input
                            id="name"
                            data-testid={`name-${index}`}
                            type="text"
                            className="form-control height-45"
                            placeholder="Ex: Name"
                            maxLength="50"
                            value={personality.name}
                            onChange={(e) => handleInputChange(e, index)}
                        />
                    </div>

                    <div className="col-md-6">
                        <label htmlFor="wikipediaUrl" className="form-label w-100 font-500 mb-1">Wikipedia URL</label>
                        <input
                            id="wikipediaUrl"
                            data-testid={`wikipediaUrl-${index}`}
                            type="text"
                            className="form-control height-45"
                            placeholder="Ex: Wikipedia URL"
                            maxLength="100"
                            value={personality.wikipediaUrl}
                            onChange={(e) => handleInputChange(e, index)}
                        />
                    </div>
                            
                    <div className="col-md-12">
                        <label className="form-label m-0 mb-1 font-16 font-500 w-100" htmlFor={`profilePicture-${index}`}>Profile Picture</label>
                        <input
                        type="file"
                        id={`profilePicture-${index}`}
                        className="d-none"
                        onChange={(e) => handleFileChange(e, index)}
                        accept=".png, .jpg, .jpeg"
                        />

                        <label className="upload_section w-100" htmlFor={`profilePicture-${index}`}>
                            <div className="row g-2 align-items-center">
                                <div className="col-12">
                                    <div className="cloud-wrap px-3 px-sm-5 d-flex justify-content-center">
                                        <div className="cloud-img">
                                            <img src="https://www.univariety.xyz/prototype/school_dashboard/images/cloud-computing.svg" alt="" />
                                        </div>
                                        <div>Drag and drop here or <u className="text-skyblue">Browse</u></div>
                                    </div>
                                </div>
                            </div>
                        </label>
                        <div className="font-14 text-dark-blue opacity-7 text-start mt-0">Only png, jpg, jpeg files. Best resolution 200 x 200</div>

                        {/* Image Preview Section */}
                        {personality.profilePictureLink && (
                            <>
                                <div>
                                    <img
                                        src={personality.profilePictureLink}
                                        alt="Profile Preview"
                                        width="100"
                                        height="100"
                                        style={{ objectFit: 'cover', borderRadius: '50%' }}
                                    />
                                </div>

                                {/* View Image Link */}
                                <button
                                    type="button"
                                    className="btn btn-link text-primary"
                                    onClick={() => openImageModal(personality.profilePictureLink)}
                                >
                                    View Image
                                </button>
                            </>
                        )}
                    </div>

                    <div className="col-md-6">
                        <label htmlFor="famousFor" className="form-label w-100 font-500 mb-1">Famous For</label>
                        <input
                            id="famousFor"
                            data-testid={`famousFor-${index}`}
                            type="text"
                            className="form-control height-45"
                            placeholder="Ex: Famous For"
                            maxLength="100"
                            value={personality.famousFor}
                            onChange={(e) => handleInputChange(e, index)}
                        />
                    </div>

                    <div className="col-md-6">
                        <label htmlFor="awards" className="form-label w-100 font-500 mb-1">Awards</label>
                        <input
                            id="awards"
                            data-testid={`awards-${index}`}
                            type="text"
                            className="form-control height-45"
                            placeholder="Ex: Awards"
                            maxLength="100"
                            value={personality.awards}
                            onChange={(e) => handleInputChange(e, index)}
                        />
                    </div>

                    <div className="col-12">
                        <hr className="mt-0" />
                    </div>
                </div>
            ))}

            <div className="col-12">
                {personalities.length < 8 && (
                    <button className="btn-green pe-3" onClick={addMorePersonalities}>
                        <i className="icon-plus me-2"></i> Add More
                    </button>
                )}
            </div>

            <div className="col-md-12">
                <div className="action_btns mt-3">
                    <div className="row g-2 align-items-center justify-content-center">
                        <div className="col-auto">
                            <button type="button" className="btn btn-green height-45 px-5" onClick={handleSave}>
                                <span>Save</span>
                            </button>
                        </div>
                        <div className="col-auto">
                            <button type="button" className="btn btn-border-gray height-45 px-5" onClick={handleCancel}>
                                <span>Cancel </span>
                            </button>
                        </div>
                    </div>
                </div>
                {saveSuccess && <div className="alert alert-success mt-3">{saveSuccess}</div>}
                {saveError && <div className="alert alert-danger mt-3">{saveError}</div>}
            </div>

            {/* Image Modal */}
            {modalImage && (
                <div className="modal show" style={{ 
                    position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0, 0, 0, 0.8)', display: 'flex', justifyContent: 'center', alignItems: 'center' 
                }}>
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Image Preview</h5>
                                <button type="button" className="btn-close" onClick={closeImageModal}></button>
                            </div>
                            <div className="modal-body">
                                <img src={modalImage} alt="Preview" className="img-fluid" />
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FamousPersonality;
